import { useEffect, useMemo, useState } from "react";
import ModalV2 from "../../ModalV2";
import CodeEditor from "../../CodeEditor";
import useModal from "../../../hooks/useModal";
import useAbis from "../../../hooks/useAbis";
import toast from "react-hot-toast";
import { Button } from "@oc-labs/wt-storybook";
import { X, AlertTriangle, CheckCircle2 } from "lucide-react";
import PropTypes from "prop-types";

const empty = { name: "", abi: "[]" };

const stringifyAbi = (raw) => {
  if (raw == null) return "";
  if (typeof raw === "string") {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return "";
  }
};

export default function UpsertAbiModal({ onUpsert }) {
  const { adminAbiByName, adminCreateAbi, adminUpdateAbi } = useAbis();
  const { close, data, isOpen } = useModal("upsert-abi-modal");
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loadingAbi, setLoadingAbi] = useState(false);

  const isEdit = Boolean(data?.name && !data?.__isNew);
  const isPrefill = Boolean(data && data.__isNew);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm({ name: data.name, abi: stringifyAbi(data.abi) });
      if (data.abi == null) {
        setLoadingAbi(true);
        adminAbiByName(data.name)
          .then((res) => {
            const abi = res?.abi ?? res?.body ?? res ?? [];
            setForm({ name: data.name, abi: stringifyAbi(abi) });
          })
          .catch((err) =>
            toast.error(err?.response?.data?.message || err.message || "Failed to load ABI"))
          .finally(() => setLoadingAbi(false));
      }
    } else if (isPrefill) {
      setForm({
        name: data.name || "",
        abi: stringifyAbi(data.abi) || "[]",
      });
    } else {
      setForm(empty);
    }
  }, [isOpen, data?.name]);

  const validation = useMemo(() => {
    const code = (form.abi || "").trim();
    if (!code) return { ok: false, message: "ABI is empty" };
    try {
      const parsed = JSON.parse(code);
      if (!Array.isArray(parsed)) return { ok: false, message: "ABI must be a JSON array" };
      return { ok: true, message: `${parsed.length} entries` };
    } catch (err) {
      return { ok: false, message: `Invalid JSON: ${err.message}` };
    }
  }, [form.abi]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!validation.ok) {
      toast.error(validation.message);
      return;
    }
    setSaving(true);
    try {
      const abi = JSON.parse(form.abi);
      if (isEdit) {
        await adminUpdateAbi(form.name, { abi });
        toast.success("ABI updated");
      } else {
        await adminCreateAbi({ name: form.name.trim(), abi });
        toast.success("ABI created");
      }
      onUpsert?.();
      close();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to save ABI");
    } finally {
      setSaving(false);
    }
  };

  const handleFormat = () => {
    if (!validation.ok) {
      toast.error(validation.message);
      return;
    }
    setForm({ ...form, abi: JSON.stringify(JSON.parse(form.abi), null, 2) });
  };

  return (
    <ModalV2 name="upsert-abi-modal" className="w-[min(1100px,95vw)] h-[85vh]">
      <div className="flex flex-col h-full w-full">
        <div className="shrink-0 p-8 border-b border-gray-300 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-2">
              <h1 className="text-white text-3xl font-black leading-tight tracking-[-0.033em]">
                {isEdit ? "Edit ABI" : "New ABI"}
              </h1>
              <p className="text-blue-100 text-base font-normal">
                Define the JSON ABI used to interact with your contract.
              </p>
            </div>
            <button className="text-gray-200 hover:text-white transition-colors" onClick={() => close()}>
              <X />
            </button>
          </div>
        </div>

        <div className="shrink-0 px-8 pt-6">
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">
              Name {isEdit && <span className="text-xs text-gray-500 font-normal">(read-only)</span>}
            </p>
            <input
              className={`form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 h-12 placeholder:text-gray-400 px-4 text-base font-normal ${
                isEdit ? "bg-gray-100 cursor-not-allowed" : "bg-white"
              }`}
              placeholder="e.g. ERC20"
              value={form.name}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
        </div>

        <div className="flex-1 min-h-0 px-8 pt-4 pb-4 flex flex-col gap-2">
          <div className="flex items-center justify-between shrink-0">
            <p className="text-gray-900 text-sm font-medium">ABI (JSON Array)</p>
            <div className="flex items-center gap-3">
              {loadingAbi ? (
                <span className="text-xs text-gray-500">Loading…</span>
              ) : validation.ok ? (
                <span className="flex items-center gap-1 text-xs text-green-700">
                  <CheckCircle2 size={14} /> {validation.message}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-red-700">
                  <AlertTriangle size={14} /> {validation.message}
                </span>
              )}
              <button
                onClick={handleFormat}
                type="button"
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Format JSON
              </button>
            </div>
          </div>
          <CodeEditor
            value={form.abi}
            language="json"
            onChange={(abi) => setForm({ ...form, abi })}
            placeholder='[{"type":"function","name":"transfer", ... }]'
            fillHeight
            className="flex-1 min-h-0"
            readOnly={loadingAbi}
          />
        </div>

        <div className="shrink-0 bg-white border-t border-gray-200 px-8 py-4 flex justify-end gap-4">
          <Button type="tertiary" onClick={() => close()}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave} disabled={saving || loadingAbi}>
            {saving ? "Saving…" : isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </ModalV2>
  );
}

UpsertAbiModal.propTypes = {
  onUpsert: PropTypes.func,
};
